import { Button, CircularProgress, Paper, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { User } from '../../types/user.type';
import { UserRoles } from '../../types/user-role.type';
import { UpdateUserFields, useUserEditForm } from '../../hooks/use-user-edit-form.hook';
import { getAssignableRoles, isStaffAccountProtected } from '../../utils/get-assignable-roles.util';
import { UserProfileCard } from './UserProfileCard.component';
import { UserAddressesCard } from './UserAddressesCard.component';
import PhoneNumberField from '../../../shared/components/Forms/PhoneNumberField.component';
import { useAdminAuth } from '../../../shared/hooks/use-admin-auth.hook';

const OWN_ROLE_LOCK_MESSAGE = 'Vous ne pouvez pas changer votre propre rôle';
const STAFF_ACCOUNT_LOCK_MESSAGE = 'Seul un super administrateur peut modifier ce compte';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& h4': {
      margin: 0,
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  actionsHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  body: {
    display: 'flex',
    gap: '2rem',
  },
  column: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& > section': {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
  },
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  flexInputs: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
}));

const getRoleLockMessage = ({ isOwnAccount, isStaffLocked }: { isOwnAccount: boolean; isStaffLocked: boolean }) => {
  if (isOwnAccount) return OWN_ROLE_LOCK_MESSAGE;
  if (isStaffLocked) return STAFF_ACCOUNT_LOCK_MESSAGE;
  return undefined;
};

interface Props {
  user: User;
  isUpdating: boolean;
  updateUser: UpdateUserFields;
}

export default function UserInformationsTab({ user, isUpdating, updateUser }: Props) {
  const classes = useStyles();
  const { role, userId } = useAdminAuth();

  const { isEditing, updatedUser, fieldErrors, handleFieldChange, handleCancelEditing, handleSave } =
    useUserEditForm(user);
  const hasFieldError = Object.keys(fieldErrors).length > 0;

  const actorRole = role ?? UserRoles.ADMIN;
  const actorId = userId ?? '';
  const { roles, isLocked: isRoleLocked } = getAssignableRoles({ actorRole, actorId, target: user });
  const isStaffLocked = isStaffAccountProtected({ actorRole, target: user });
  const roleHelperText = getRoleLockMessage({ isOwnAccount: actorId === user.id, isStaffLocked });

  return (
    <section className={classes.root}>
      <div className={classes.actionsHeader}>
        <Button size="small" variant="text" onClick={handleCancelEditing} disabled={!isEditing || isUpdating}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          onClick={() => handleSave(updateUser)}
          disabled={!isEditing || isUpdating || hasFieldError}
          startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      <section className={classes.body}>
        <div className={classes.column}>
          <section>
            <h4>Identité</h4>
            <Paper className={classes.paperContent}>
              <section className={classes.flexInputs}>
                <TextField
                  id="user-firstname"
                  label="Prénom"
                  type="text"
                  size="small"
                  variant="outlined"
                  error={Boolean(fieldErrors.firstname)}
                  helperText={fieldErrors.firstname}
                  value={updatedUser.firstname}
                  onChange={(event) => handleFieldChange({ field: 'firstname', value: event.target.value })}
                />
                <TextField
                  id="user-lastname"
                  label="Nom"
                  type="text"
                  size="small"
                  variant="outlined"
                  error={Boolean(fieldErrors.lastname)}
                  helperText={fieldErrors.lastname}
                  value={updatedUser.lastname}
                  onChange={(event) => handleFieldChange({ field: 'lastname', value: event.target.value })}
                />
              </section>
              <section className={classes.flexInputs}>
                <TextField
                  id="user-email"
                  label="Email"
                  type="email"
                  size="small"
                  variant="outlined"
                  disabled={isStaffLocked}
                  helperText={isStaffLocked ? STAFF_ACCOUNT_LOCK_MESSAGE : fieldErrors.email}
                  error={Boolean(fieldErrors.email)}
                  value={updatedUser.email}
                  onChange={(event) => handleFieldChange({ field: 'email', value: event.target.value })}
                />
                <PhoneNumberField
                  id="user-phone-number"
                  value={updatedUser.phoneNumber ?? ''}
                  error={Boolean(fieldErrors.phoneNumber)}
                  helperText={fieldErrors.phoneNumber}
                  onChange={(digits) => handleFieldChange({ field: 'phoneNumber', value: digits })}
                />
              </section>
              <TextField
                id="user-information"
                label="Complément de profil"
                type="text"
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                value={updatedUser.information ?? ''}
                onChange={(event) => handleFieldChange({ field: 'information', value: event.target.value })}
              />
            </Paper>
          </section>

          <UserProfileCard
            user={user}
            updatedUser={updatedUser}
            roles={roles}
            isRoleLocked={isRoleLocked}
            roleHelperText={isRoleLocked ? roleHelperText : undefined}
            ecoPointsError={fieldErrors.ecoPoints}
            onFieldChange={handleFieldChange}
          />
        </div>

        <div className={classes.column}>
          <UserAddressesCard
            addresses={user.addresses ?? []}
            isUpdating={isUpdating}
            isLocked={isEditing}
            updateUser={updateUser}
          />
        </div>
      </section>
    </section>
  );
}
